/**
 * Parses image URLs from PostgreSQL array format
 * Handles both string arrays and stringified array representations
 *
 * Examples:
 * - Input: '"{https://example.com/image.jpg}"'
 * - Output: ['https://example.com/image.jpg']
 *
 * - Input: ['https://example.com/image.jpg']
 * - Output: ['https://example.com/image.jpg']
 */
export function parseImageUrls(images) {
    if (!images)
        return [];
    // If it's already an array, process each element
    if (Array.isArray(images)) {
        const results = [];
        for (const img of images) {
            if (typeof img !== 'string')
                continue;
            const trimmed = img.trim();
            // Handle PostgreSQL array format within array element: '{url}'
            if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
                const content = trimmed.slice(1, -1); // Remove { }
                const urls = content
                    .split(',')
                    .map(url => url.trim().replace(/^"|"$/g, '')) // Remove quotes
                    .filter(url => url.length > 0 && url.startsWith('http'));
                results.push(...urls);
            }
            // Handle direct URL
            else if (trimmed.startsWith('http')) {
                results.push(trimmed);
            }
        }
        return results;
    }
    // If it's a string, try to parse it
    if (typeof images === 'string') {
        const trimmed = images.trim();
        // Handle PostgreSQL array format: "{url1, url2}"
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            const content = trimmed.slice(1, -1); // Remove { }
            return content
                .split(',')
                .map(url => url.trim().replace(/^"|"$/g, '')) // Remove quotes
                .filter(url => url.length > 0 && url.startsWith('http'));
        }
        // Handle JSON array format: ["url1", "url2"]
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) {
                    return parsed
                        .map(url => (typeof url === 'string' ? url.trim() : ''))
                        .filter(url => url.length > 0 && url.startsWith('http'));
                }
            }
            catch {
                // Fall through to return empty array
            }
        }
        // If it's a single URL string
        if (trimmed.startsWith('http')) {
            return [trimmed];
        }
    }
    return [];
}
/**
 * Gets the first valid image URL from the images array
 */
export function getFirstImageUrl(images) {
    const parsed = parseImageUrls(images);
    return parsed.length > 0 ? (parsed[0] ?? null) : null;
}
